import { LinkedEntityType } from "../../models/diaryTypes";
import type { DiaryLinkDto } from "../../models/diaryTypes";
import type { ArtworkStub } from "../../models/Artwork";
export function getDiaryLinkRoute(link: DiaryLinkDto): string | null {
    switch (link.entityType) {
        case LinkedEntityType.Artwork:
            return `/artwork/${link.entityId}`;
        default:
            return null;
    }
}

export function getArtworkIdsFromLinks(links: DiaryLinkDto[]): number[] {
    return links
        .filter(l => l.entityType === LinkedEntityType.Artwork)
        .map(l => Number(l.entityId))
        .filter(n => Number.isInteger(n) && n > 0);
}

export function setArtworkLinks(links: DiaryLinkDto[], selectedArtworkIds: number[]): DiaryLinkDto[] {
    const nonArtwork = links.filter(l => l.entityType !== LinkedEntityType.Artwork);

    // normalise and de-dupe ids
    const ids = Array.from(new Set(selectedArtworkIds.filter(n => Number.isInteger(n) && n > 0)));

    const artworkLinks: DiaryLinkDto[] = ids.map(id => {
        // if a link already existed, keep its label/reference fields
        const existing = links.find(l => l.entityType === LinkedEntityType.Artwork && Number(l.entityId) === id);

        return {
            entityType: LinkedEntityType.Artwork,
            entityId: String(id),
            label: existing?.label ?? null,
            amount: existing?.amount ?? null,
            currency: existing?.currency ?? null,
            reference: existing?.reference ?? null,
        };
    });

    return [...nonArtwork, ...artworkLinks];
}

export function getSelectedArtworkStubs(all: ArtworkStub[], selectedIds: number[]): ArtworkStub[] {
    const set = new Set(selectedIds);
    return all.filter(a => set.has(a.artworkId));
}

export function buildArtworkLabel(a: ArtworkStub): string {
    // Consistent, readable label.
    const title = (a.artWorkTitle ?? "").trim();
    const artist = (a.artistName ?? "").trim();
    if (title && artist) return `${title} — ${artist}`;
    return title || artist || `Artwork #${a.artworkId}`;
}

export function artworkStubToLink(a: ArtworkStub): DiaryLinkDto {
    return {
        entityType: LinkedEntityType.Artwork,
        entityId: String(a.artworkId),
        label: buildArtworkLabel(a),

        // Optional, but useful metadata
        // reference: a.locationPath,
        // amount: a.estimatedValue,
        // currency: a.currencyCode,
    };
}

export function mergeArtworkLinksPreservingOtherLinks(
    existingLinks: DiaryLinkDto[],
    selectedArtworkStubs: ArtworkStub[]
): DiaryLinkDto[] {
    const nonArtwork = existingLinks.filter(l => l.entityType !== LinkedEntityType.Artwork);

    // Preserve any existing metadata fields for still-selected artworks
    const existingArtworkById = new Map<number, DiaryLinkDto>();
    for (const l of existingLinks) {
        if (l.entityType !== LinkedEntityType.Artwork) continue;
        const id = Number(l.entityId);
        if (Number.isInteger(id) && id > 0) existingArtworkById.set(id, l);
    }

    const deduped = new Map<number, ArtworkStub>();
    for (const a of selectedArtworkStubs) deduped.set(a.artworkId, a);

    const artworkLinks: DiaryLinkDto[] = Array.from(deduped.values()).map(a => {
        const existing = existingArtworkById.get(a.artworkId);
        const fresh = artworkStubToLink(a);

        return {
            ...fresh,
            ...(existing
                ? {
                    amount: existing.amount ?? fresh.amount,
                    currency: existing.currency ?? fresh.currency,
                    reference: existing.reference ?? fresh.reference,
                }
                : null),
        };
    });

    return [...nonArtwork, ...artworkLinks];
}

